// Die Sitzung: Solo oder Online, und der Weg dazwischen.
//
// Far Mining bleibt **ohne Backend spielbar**. Das ist keine Nachlaessigkeit,
// sondern eine Zusage: das Spiel ist ein Inkrementalspiel fuer nebenbei, und
// ein Ladebildschirm mit „Server nicht erreichbar" waere das Ende der
// Kernschleife.
//
// Der Zustandsuebergang ist deshalb einseitig gerichtet und ohne Rueckfrage:
// beim Start wird ein Server versucht; klappt es, gilt sein Spielstand. Klappt
// es nicht, laeuft Solo mit dem Zwischenspeicher aus `localStorage`.
//
// **Was es bewusst nicht gibt:** ein Zusammenfuehren beider Staende. Zwei
// Historien zu verschmelzen erzeugt Faelle, die niemand testet — und der
// Spieler merkt den Verlust erst spaeter.

import { adoptBalancing } from '../data/balancing';
import { loadLocal, saveLocal, store } from '../state/store';
import { Api, ApiFehler, type PlayerInfo } from './api';

export type Mode = 'solo' | 'online';

/** Wie ein Zug: gelungen, oder abgelehnt mit einem Grund in Spielersprache. */
export interface AccountResult {
  ok: boolean;
  message?: string;
}

function abgelehnt(error: unknown): AccountResult {
  return {
    ok: false,
    message: error instanceof ApiFehler
      ? error.message
      : 'Der Server ist gerade nicht erreichbar.',
  };
}

export interface SessionInfo {
  mode: Mode;
  playerName: string;
  fieldName: string;
}

export interface SessionOptions {
  baseUrl: string;
  /** Erzwingt Solo, ohne den Server zu fragen (Kommandozeile `?solo=1`). */
  forceSolo?: boolean;
}

export class Session {
  readonly api: Api;
  mode: Mode = 'solo';
  playerName = 'Rettungskapsel';
  player: PlayerInfo | null = null;

  constructor(private readonly options: SessionOptions) {
    this.api = new Api(options.baseUrl);
  }

  /** Ein Gastkonto haengt am Browser. Genau darum geht es beim Hinweis. */
  get isGuest(): boolean {
    return this.mode === 'online' && (this.player?.guest ?? true);
  }

  /**
   * Aus dem laufenden Gastkonto ein richtiges machen.
   *
   * Der Spielstand bleibt, wo er ist — er haengt am Spieler, und der wechselt
   * nicht. Deshalb wird hier auch nichts neu geladen.
   */
  async register(username: string, password: string): Promise<AccountResult> {
    try {
      this.adopt(await this.api.register(username, password, this.playerName));
      return { ok: true };
    } catch (error) {
      return abgelehnt(error);
    }
  }

  /**
   * Anmelden — und **der Serverstand gilt**.
   *
   * Kein Verschmelzen mit dem, was gerade lokal liegt: zwei Historien zu
   * vereinen erzeugt Faelle, die niemand testet, und der Spieler merkt den
   * Verlust erst spaeter (`NET-001`). Der Dialog sagt das vorher.
   */
  async login(username: string, password: string): Promise<AccountResult> {
    try {
      this.adopt(await this.api.login(username, password));
      await this.adoptServerSave();
      return { ok: true };
    } catch (error) {
      return abgelehnt(error);
    }
  }

  /** Abmelden: Token vergessen und als frischer Gast weiterspielen. */
  async logout(): Promise<AccountResult> {
    try {
      this.api.forgetToken();
      this.player = null;
      this.adopt(await this.api.ensureSession());
      await this.adoptServerSave();
      return { ok: true };
    } catch (error) {
      return abgelehnt(error);
    }
  }

  private async adoptServerSave(): Promise<void> {
    const payload = await this.api.savegame();
    store.adopt(payload.save, payload.revision);
    store.authoritative = false;
    this.mode = 'online';
  }

  private adopt(player: PlayerInfo | null): void {
    this.player = player;
    if (player?.name) this.playerName = player.name;
  }

  /**
   * Verbinden, wenn moeglich. Gibt zurueck, in welchem Modus gespielt wird.
   *
   * Wirft **nicht**: ein nicht erreichbarer Server ist ein gueltiger Zustand.
   */
  async start(): Promise<SessionInfo> {
    if (this.options.forceSolo) return this.goSolo();

    try {
      const data = await this.api.balancing();
      adoptBalancing(data);
    } catch {
      return this.goSolo();               // ohne Tabellen vom Server: Abzug benutzen
    }

    try {
      const player = await this.api.ensureSession();
      const payload = await this.api.savegame();
      store.adopt(payload.save, payload.revision);
      store.authoritative = false;
      this.mode = 'online';
      this.playerName = player?.name || 'Pilotin';
      this.player = player;
      return { mode: 'online', playerName: this.playerName, fieldName: 'Randfeld' };
    } catch {
      return this.goSolo();
    }
  }

  private goSolo(): SessionInfo {
    this.mode = 'solo';
    store.authoritative = true;
    if (!loadLocal()) saveLocal();
    return { mode: 'solo', playerName: this.playerName, fieldName: 'Solo-Feld' };
  }
}
